import React, { useState, useEffect } from 'react';
import { ArrowLeft, Grid, Film, Bookmark, MoreHorizontal, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { feedAPI, interactionsAPI } from '../services/api';
import useInstanceStore from '../stores/instanceStore';
import InstagramCarousel from './InstagramCarousel';
import { toast } from 'sonner';

const ProfilePage = ({ entity, instanceId: propsInstanceId, currentUser, onBack }) => {
  const { selectedInstanceId } = useInstanceStore();
  const instanceId = propsInstanceId || selectedInstanceId;
  const [isFollowing, setIsFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');
  const [entityPosts, setEntityPosts] = useState([]);
  const [entityReels, setEntityReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // View mode state for posts tab: 'grid' (thumbnail view) or 'feed' (scrollable posts)
  const [viewMode, setViewMode] = useState('grid');
  const [selectedPostId, setSelectedPostId] = useState(null);

  // Fetch entity's profile data including posts
  useEffect(() => {
    const fetchEntityData = async () => {
      if (!entity?.id) return;

      setLoading(true);
      setError(null);
      try {
        const profileData = await feedAPI.getEntity(entity.id);
        console.log('📊 Entity profile data loaded:', {
          entityId: entity.id,
          entityName: profileData.entity?.name,
          postsCount: profileData.posts?.length || 0,
          totalPosts: profileData.total_posts || 0
        });

        // Update posts with data from API
        setEntityPosts(profileData.posts || []);

        // Reels would come from a separate endpoint in future
        setEntityReels(profileData.reels || []);
      } catch (err) {
        console.error('Error fetching entity profile:', err);
        setError('Failed to load profile. Please try again.');
        setEntityPosts([]);
        setEntityReels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEntityData();
  }, [entity?.id]);

  if (!entity) return null;

  const handleFollow = () => {
    setIsFollowing(!isFollowing);
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0'; // Handle undefined/null values
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return String(num);
  };

  // Scroll to selected post when entering feed view
  useEffect(() => {
    if (viewMode === 'feed' && selectedPostId) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        const postElement = document.getElementById(`post-${selectedPostId}`);
        if (postElement) {
          postElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [viewMode, selectedPostId]);

  // Handlers for like/save interactions in feed view
  const handleLike = async (postId, liked) => {
    // Optimistic update
    setEntityPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, liked_by_user: liked, likes: liked ? (p.likes || 0) + 1 : Math.max(0, (p.likes || 0) - 1) } : p
    ));

    try {
      await interactionsAPI.create(postId, 'like');
      if (liked) {
        toast.success('Post liked!');
      }
    } catch (error) {
      // Revert on error
      setEntityPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, liked_by_user: !liked, likes: liked ? Math.max(0, (p.likes || 0) - 1) : (p.likes || 0) + 1 } : p
      ));
      toast.error('Failed to like post');
      console.error('Like error:', error);
    }
  };

  const handleSave = async (postId, saved) => {
    // Optimistic update
    setEntityPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, saved_by_user: saved } : p
    ));

    try {
      await interactionsAPI.create(postId, 'save');
      if (saved) {
        toast.success('Post saved!');
      } else {
        toast.success('Post removed from saved');
      }
    } catch (error) {
      // Revert on error
      setEntityPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, saved_by_user: !saved } : p
      ));
      toast.error('Failed to save post');
      console.error('Save error:', error);
    }
  };

  const handleComment = (postId) => {
    console.log('Comment on post:', postId);
    toast.info('Comments coming soon!');
  };

  const handlePostDeleted = (deletedPostId) => {
    setEntityPosts(prev => prev.filter(p => p.id !== deletedPostId));
    toast.success('Post deleted successfully');
  };

  // Show loading state while fetching entity data
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Show error state if fetch failed
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center p-6">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Profile</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={onBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-bold text-lg">{entity.name || 'Unknown'}</h1>
            <p className="text-sm text-gray-600">{entityPosts.length || 0} posts</p>
          </div>
        </div>
        {/* <Button variant="ghost" size="sm">
          <MoreHorizontal className="w-5 h-5" />
        </Button> */}
      </div>

      {/* Profile Info */}
      <div className="p-4">
        <div className="flex items-start gap-4 mb-4">
          <Avatar className="w-20 h-20">
            <AvatarImage src={entity.avatar_url} alt={entity.name} />
            <AvatarFallback className="text-2xl">{entity.name?.charAt(0) || '?'}</AvatarFallback>
          </Avatar>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="font-bold text-xl">{entity.name || 'Unknown'}</h2>
              {entity.verified && (
                <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">✓</span>
                </div>
              )}
            </div>
            <p className="text-gray-600 text-sm mb-3">{entity.handle || `@${entity.name?.toLowerCase()?.replace(/\s+/g, '_') || 'chapter'}`}</p>
{/*             
            <div className="flex gap-6 mb-3">
              <div className="text-center">
                <p className="font-bold text-lg">{formatNumber(entityPosts.length || 0)}</p>
                <p className="text-gray-600 text-xs">Posts</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-lg">{formatNumber(entity.followers || 0)}</p>
                <p className="text-gray-600 text-xs">Followers</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-lg">{formatNumber(entity.following || 0)}</p>
                <p className="text-gray-600 text-xs">Following</p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button 
                variant={isFollowing ? "outline" : "default"}
                size="sm"
                onClick={handleFollow}
                className="flex-1"
              >
                {isFollowing ? 'Following' : 'Follow'}
              </Button>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  // TODO: Implement chat functionality in next step
                  alert(`Chat with ${entity.name || 'Unknown'} - Coming soon in Phase 2C!`);
                }}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Chat
              </Button>
              <Button variant="outline" size="sm">
                <MoreHorizontal className="w-5 h-5" />
              </Button>
            </div> */}
          </div>
        </div>

        <div className="mb-4">
          <p className="text-gray-800 text-sm leading-relaxed">{entity.bio || ''}</p>
          {entity.type && (
            <div className="mt-2">
              <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full capitalize">
                {entity.type}
              </span>
            </div>
          )}
        </div>

        {/* Highlights by Chapter - Social media-style highlights */}
        {/* <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Highlights by Chapter</h3>
          <div className="flex gap-4 overflow-x-auto pb-2">

            {[
              { id: 'act1', title: 'Act I', cover: 'https://picsum.photos/80/80?random=21', count: 3 },
              { id: 'act2', title: 'Act II', cover: 'https://picsum.photos/80/80?random=22', count: 5 },
              { id: 'balcony', title: 'Balcony Scene', cover: 'https://picsum.photos/80/80?random=23', count: 2 },
              { id: 'quotes', title: 'Famous Quotes', cover: 'https://picsum.photos/80/80?random=24', count: 8 }
            ].map((highlight) => (
              <div 
                key={highlight.id}
                className="flex flex-col items-center min-w-[80px] cursor-pointer"
                onClick={() => {
                  // TODO: Open highlight story sequence
                  alert(`Opening ${highlight.title} highlight stories (${highlight.count} stories)`);
                }}
              >
                <div className="relative mb-2">
                  <div className="w-16 h-16 rounded-full p-0.5 bg-gradient-to-r from-purple-400 to-pink-400">
                    <div className="w-full h-full rounded-full bg-white p-0.5">
                      <img 
                        src={highlight.cover}
                        alt={highlight.title}
                        className="w-full h-full rounded-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {highlight.count}
                  </div>
                </div>
                <span className="text-xs text-gray-700 text-center max-w-[80px] truncate">
                  {highlight.title}
                </span>
              </div>
            ))}
          </div>
        </div> */}
      </div>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full justify-center border-t border-gray-100 bg-white rounded-none">
          <TabsTrigger value="posts" className="flex-1 flex items-center gap-2">
            <Grid className="w-4 h-4" />
            <span className="hidden sm:inline">Posts</span>
          </TabsTrigger>
          <TabsTrigger value="reels" className="flex-1 flex items-center gap-2">
            <Film className="w-4 h-4" />
            <span className="hidden sm:inline">Reels</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="posts" className="mt-0">
          {viewMode === 'grid' ? (
            /* Grid View - Thumbnail Preview */
            entityPosts.length > 0 ? (
              <div className="grid grid-cols-3 gap-1 p-4">
                {entityPosts.map((post) => (
                  <div
                    key={post.id}
                    className="aspect-square bg-gray-100 relative cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => {
                      setSelectedPostId(post.id);
                      setViewMode('feed');
                    }}
                  >
                    {post.media_url ? (
                      <img
                        src={post.media_url}
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                        <span className="text-xs text-gray-500 text-center px-2">
                          {post.title?.substring(0, 20)}...
                        </span>
                      </div>
                    )}

                    {/* Overlay with post stats */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 flex items-end">
                      <div className="w-full p-2 text-white text-xs opacity-0 hover:opacity-100 transition-opacity">
                        <div className="flex items-center gap-2">
                          <span>❤️ {post.likes || 0}</span>
                          <span>💬 {post.comments || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Grid className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No posts yet</h3>
                <p className="text-gray-600 text-sm">When {entity.name || 'this entity'} shares posts, they'll appear here</p>
              </div>
            )
          ) : (
            /* Feed View - Scrollable Posts with CarouselPost */
            <div className="bg-white">
              {/* Back to Grid Button - Sticky Header */}
              <div className="sticky top-0 bg-white border-b border-gray-100 p-4 z-10">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Grid
                </Button>
              </div>

              {/* Scrollable Feed of Posts */}
              <div className="space-y-0">
                {entityPosts.map((post) => (
                  <div
                    key={post.id}
                    id={`post-${post.id}`}
                    data-post-id={post.id}
                    className="p-4"
                  >
                    <InstagramCarousel
                      post={post}
                      entity={post.entity || entity}
                      onLike={handleLike}
                      onSave={handleSave}
                      onComment={handleComment}
                      onProfileClick={() => {
                        // Already on this entity's profile, no action needed
                        console.log('Already viewing this entity profile');
                      }}
                      onPostDeleted={handlePostDeleted}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="reels" className="mt-0">
          {entityReels.length > 0 ? (
            <div className="grid grid-cols-3 gap-1 p-4">
              {entityReels.map((reel) => (
                <div key={reel.id} className="aspect-[9/16] relative bg-gray-100 rounded-lg overflow-hidden">
                  <img 
                    src={reel.thumbnail_url} 
                    alt={reel.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                    <Film className="w-6 h-6 text-white" />
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-white text-xs font-medium line-clamp-2">
                      {reel.title}
                    </p>
                  </div>
                  <div className="absolute top-2 right-2">
                    <span className="bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                      {formatNumber(reel.views)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Film className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No reels yet</h3>
              <p className="text-gray-600 text-sm">When {entity.name || 'this entity'} shares reels, they'll appear here</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProfilePage;